import { useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { Binary, BarChart3, Sliders, TrendingUp, Globe, Github, Moon, Sun } from 'lucide-react'
import { useThemeStore } from '@/stores/themeStore'
import { cn } from '@/lib/utils'

const navItems = [
  { to: '/sp800-22', label: 'SP 800-22', icon: Binary, description: 'NIST Statistical Tests' },
  { to: '/sp800-90b', label: 'SP 800-90B', icon: BarChart3, description: 'Entropy Estimation' },
  { to: '/tunable-bias', label: 'Tunable Bias', icon: Sliders, description: 'Biased RNG Analysis' },
  { to: '/gaussian', label: 'Gaussian', icon: TrendingUp, description: 'Distribution Testing' },
  { to: '/api-docs', label: 'API', icon: Globe, description: 'REST API' },
]

export function Layout() {
  const { isDark, toggle } = useThemeStore()
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <div className="min-h-screen flex flex-col overflow-x-clip">
      {/* Skip to main content link for keyboard/screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-background focus:px-4 focus:py-2 focus:rounded-md focus:border focus:text-sm"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-2.5 font-mono font-bold text-lg text-primary hover:opacity-80 transition-opacity">
              <img
                src={isDark ? '/logos/icon-dark.svg' : '/logos/icon-light.svg'}
                alt="BitAssay"
                className="h-9 w-9 rounded-lg"
              />
              BitAssay
            </NavLink>

            {/* Navigation tabs */}
            <nav className="hidden md:flex items-center gap-1" role="navigation" aria-label="Main navigation">
              {navItems.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={toggle}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <a
                href="https://github.com/zhature/BitAssay"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="GitHub"
              >
                <Github className="h-4 w-4" />
              </a>
              <a
                href="https://zhature.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="zhature.com"
              >
                <img src="/favicon-zhature-20260324-135240.svg" alt="zhature.com" className="h-4 w-4 rounded-sm" />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile nav */}
      <nav className="md:hidden border-b border-border bg-card overflow-x-auto" role="navigation" aria-label="Mobile navigation">
        <div className="flex px-4 gap-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-1 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors',
                  isActive
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <main id="main-content" className="flex-1" role="main">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-4 px-4 text-center text-xs text-muted-foreground">
        <p>BitAssay v1.2.0 &mdash; Statistical Test Suite for Physical Random Number Generators</p>
        <p className="mt-1">All computation runs locally in your browser. No data is uploaded.</p>
        <p className="mt-1">Open-source and fully transparent &mdash; every line of code is inspectable via your browser&rsquo;s developer tools.</p>
        <p className="mt-2">
          &copy; {new Date().getFullYear()}{' '}
          <a
            href="https://zhature.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Ran Zhang
          </a>
          . All rights reserved.
        </p>
      </footer>
    </div>
  )
}
